import {Component, inject, input, OnInit, signal} from '@angular/core';
import {ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {ErrorStateMatcher} from '@angular/material/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {CountryGroup, CountryService, DEFAULT_COUNTRY_CODE} from '../../../core/services/country.service';

/**
 * Countries whose national significant number begins with the zero that other numbering plans use
 * as a trunk prefix. E.164 keeps that zero, so it must not be dropped: the Italian landline
 * 06 1234 5678 is "+390612345678", not "+39612345678".
 */
const TRUNK_PREFIX_KEEPING_COUNTRY_CODES = ['IT', 'VA'];

/**
 * A phone number field with the country calling code as a menu in front of the national number. The
 * value of the outer form control stays a single ISO E.164 number, e.g. "+41791112233".
 */
@Component({
  selector: 'app-phone-input',
  imports: [
    TranslocoPipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule
  ],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss'
})
export class PhoneInput implements ControlValueAccessor, OnInit {
  /** Transloco key of the label of the field, e.g. "form.label.mobile". */
  label = input.required<string>();

  countryService = inject(CountryService);

  countryCode = signal<string>(DEFAULT_COUNTRY_CODE);
  disabled = signal<boolean>(false);
  numberControl = new FormControl<string>('', {nonNullable: true});

  private readonly ngControl = inject(NgControl, {self: true});

  /**
   * The number field shows the error state of the outer control, which validates the composed
   * E.164 number.
   */
  errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => !!this.ngControl.invalid && !!this.ngControl.touched
  };

  private onChange: (value: string) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  constructor() {
    // Registering through NG_VALUE_ACCESSOR instead would make the injected NgControl unreachable,
    // and with it the error state of the outer control.
    this.ngControl.valueAccessor = this;
  }

  get countryGroups(): CountryGroup[] {
    return this.countryService.dialCodeGroups;
  }

  get dialCode(): string {
    return this.countryService.dialCodeOf(this.countryCode()) ?? '';
  }

  ngOnInit() {
    this.numberControl.valueChanges.subscribe(value => this.onNumberChanged(value));
  }

  writeValue(value: string | null): void {
    this.countryCode.set(DEFAULT_COUNTRY_CODE);
    this.setPhoneNumber(value ?? '');
  }

  registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);

    if (isDisabled) {
      this.numberControl.disable({emitEvent: false});
      return;
    }

    this.numberControl.enable({emitEvent: false});
  }

  selectCountry(countryCode: string): void {
    this.countryCode.set(countryCode);
    // The leading zero is a trunk prefix in one numbering plan and part of the number in the next,
    // so the national number is re-read against the country that was just picked.
    this.setNumber(this.withoutTrunkPrefix(this.numberControl.value));
    this.onTouched();
    this.emit();
  }

  onBlur(): void {
    this.onTouched();
  }

  private onNumberChanged(value: string): void {
    this.setPhoneNumber(value);
    this.emit();
  }

  /**
   * Splits a phone number into the country calling code and the national number, so that
   * "+41 79 111 22 33", "0041791112233" and "079 111 22 33" all end up as the same E.164 number.
   */
  private setPhoneNumber(value: string): void {
    const international = PhoneInput.internationalOf(value);
    const countryCode = this.countryService.countryCodeOf(international);

    if (countryCode) {
      this.countryCode.set(countryCode);
      this.setNumber(international.slice(this.dialCode.length));
      return;
    }

    // An international number that is still too short to identify a country keeps its calling code
    // until the next digit resolves it.
    if (international) {
      this.setNumber(international);
      return;
    }

    // A national number keeps its digits only, without the national trunk prefix that E.164
    // replaces with the country calling code.
    this.setNumber(this.withoutTrunkPrefix(PhoneInput.digitsOf(value)));
  }

  /**
   * Drops the national trunk prefix, which E.164 replaces with the country calling code. Numbering
   * plans that count the leading zero as part of the national number keep it, so that an Italian
   * landline stays reachable.
   */
  private withoutTrunkPrefix(number: string): string {
    if (TRUNK_PREFIX_KEEPING_COUNTRY_CODES.includes(this.countryCode())) {
      return number;
    }

    return number.replace(/^0+/, '');
  }

  private setNumber(number: string): void {
    if (number !== this.numberControl.value) {
      this.numberControl.setValue(number, {emitEvent: false});
    }
  }

  private emit(): void {
    const number = this.numberControl.value;
    if (!number) {
      this.onChange('');
      return;
    }

    this.onChange(number.startsWith('+') ? number : this.dialCode + number);
  }

  /** An international phone number as an E.164 candidate, "" for a national number. */
  private static internationalOf(value: string): string {
    const digits = PhoneInput.digitsOf(value);

    if (value.trimStart().startsWith('+')) {
      return `+${digits}`;
    }

    return digits.startsWith('00') ? `+${digits.slice(2)}` : '';
  }

  private static digitsOf(value: string): string {
    return value.replace(/\D/g, '');
  }
}
